import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { subscriptionAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, Loader2, CreditCard, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminBilling() {
  const [searchParams] = useSearchParams();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  
  const sessionId = searchParams.get('session_id');

 const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) return;

    try {
      const response = await subscriptionAPI.verify(sid);
      if (response.data.payment_status === 'paid') {
        toast.success('Abonnement activé !');
        return;
      }
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      return pollPaymentStatus(sid, attempts + 1);
    } catch (e) {
      console.error('Payment status check failed:', e);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (sessionId) {
          await pollPaymentStatus(sessionId);
        }

        const [subRes, plansRes] = await Promise.all([
          subscriptionAPI.getStatus(),
          subscriptionAPI.getPlans()
        ]);

        setSubscription(subRes.data);
        setPlans(plansRes.data);
      } catch (err) {
        console.error('Error loading billing:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [sessionId, pollPaymentStatus]);

  const handleSubscribe = async (planId) => {
    setSubscribing(true);
    
    try {
      const response = await subscriptionAPI.createCheckout(planId, window.location.origin);
      window.location.href = response.data.url;
    } catch (err) {
      toast.error('Erreur lors de la souscription');
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const isActive = subscription?.status === 'active';

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>
          Abonnement
        </h1>
        <p className="text-gray-500">Gérez votre abonnement GlassOrder</p>
      </div>

      {/* Current Status */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            Statut actuel
            {isActive ? (
              <Badge className="bg-blue-500">Actif</Badge>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-600">
                <AlertCircle className="w-3 h-3 mr-1" />
                Inactif
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isActive ? (
            <div className="space-y-2">
              <p className="text-gray-600">
                Plan : <span className="font-semibold capitalize">{subscription.plan}</span>
              </p>
              {subscription.expires_at && (
                <p className="text-sm text-gray-500">
                  Expire le : {new Date(subscription.expires_at).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          ) : (
            <p className="text-gray-600">
              Vous n'avez pas d'abonnement actif. Choisissez un plan ci-dessous pour commencer.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(plans).map(([planId, plan]) => (
          <Card 
            key={planId} 
            className={`border-2 shadow-sm ${
              subscription?.plan === planId ? 'border-[#7C3AED]' : 'border-gray-200'
            }`}
          >
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>{plan.name}</CardTitle>
              <div className="mt-2">
                <span className="text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {plan.price}€
                </span>
                <span className="text-gray-500">/mois</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                {plan.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              {subscription?.plan === planId ? (
                <Button disabled className="w-full" variant="outline">
                  Plan actuel
                </Button>
              ) : (
                <Button 
                  onClick={() => handleSubscribe(planId)}
                  disabled={subscribing}
                  className="w-full bg-[#7C3AED] hover:bg-violet-600"
                  data-testid={`subscribe-${planId}-btn`}
                >
                  {subscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  S'abonner
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Payment Info */}
      <Card className="border-0 shadow-sm bg-gray-50">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <h4 className="font-semibold mb-1">Paiement sécurisé</h4>
              <p className="text-sm text-gray-500">
                Tous les paiements sont traités par Stripe. Vos informations bancaires sont sécurisées et ne sont jamais stockées sur nos serveurs.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
